import Papa from "papaparse";
import type { Coupon } from "@/resources/coupon";

/**
 * Escapes a single CSV field. Wraps in double quotes if the value contains
 * comma, newline, or double-quote; doubles any internal double-quotes.
 */
function escapeCsvField(value: string | number): string {
  const s = String(value);
  if (/[,"\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * Creates a CSV string from headers and data rows.
 * Fields containing commas, newlines, or double-quotes are properly escaped.
 *
 * @param headers - Array of column header strings
 * @param rows - 2D array of row data, where each row is an array of strings or numbers
 * @returns CSV formatted string with headers and data rows separated by newlines
 *
 * @example
 * const headers = ['Name', 'Age'];
 * const rows = [['John', 30], ['Jane', 25]];
 * const csv = createCsvContent(headers, rows);
 * // Returns: "Name,Age\nJohn,30\nJane,25"
 */
export function createCsvContent(
  rows: (string | number)[][],
  headers?: string[],
): string {
  const escapeRow = (row: (string | number)[]) =>
    row.map((cell) => escapeCsvField(cell)).join(",");
  if (!headers || headers.length === 0) {
    return rows.map(escapeRow).join("\n");
  }
  return [
    headers.map((h) => escapeCsvField(h)).join(","),
    ...rows.map(escapeRow),
  ].join("\n");
}

/**
 * Prepends a zero-width space to prevent Excel/Sheets from auto-formatting
 * values like "1/3" as dates or fractions.
 * The zero-width space is invisible but breaks pattern matching.
 */
export function forceCsvText(value: string): string {
  if (value == null || value === "") return "";
  return "\u200B" + String(value);
}

/**
 * Downloads data as a CSV file by creating a temporary link element.
 *
 * @param content - CSV content to be downloaded
 * @param filename - Name of the file to be downloaded (should include .csv extension)
 *
 * @example
 * const csvContent = 'Name,Age\nJohn,30';
 * downloadCsv(csvContent, 'users.csv');
 */
export function downloadCsv(content: string, filename: string) {
  const blob = new Blob([`\uFEFF${content}`], { type: "text/csv" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
  link.remove();
}

export function validateCouponCsvRows(
  data: string[][],
  existingCodes: Set<string>,
): string {
  if (data.length === 0) return "";
  const codes = new Set(existingCodes);
  for (let i = 0; i < data.length; i++) {
    const row = data[i];
    if (row.length === 0) continue;
    const [couponCode, password] = row;
    if (!couponCode || !password) {
      return "Blank ticket ID or password found.";
    }
    if (row.length !== 2) {
      return "Each row must have exactly two columns.";
    }
    if (codes.has(couponCode)) {
      return "Duplicate ticket ID found.";
    }
    codes.add(couponCode);
    const validPattern = /^[A-Za-z0-9\-_]+$/;
    if (!validPattern.test(couponCode) || !validPattern.test(password)) {
      return "Ticket ID and password must be alphanumeric, hyphen, or underscore.";
    }
  }
  return "";
}

export function validateCSVInputFile(
  file: File,
  allPackageCoupons: Coupon[],
  $t: (key: string) => string,
  setImportedCoupons: (
    coupons: { couponCode: string; password: string }[],
  ) => void,
  setCSVInputErrorMessage: (msg: string) => void,
) {
  if (!file) {
    setCSVInputErrorMessage("");
    return;
  }

  Papa.parse(file, {
    complete: (result: any) => {
      const data = result.data;
      if (data.length === 0) return;

      const codes = new Set(
        allPackageCoupons.map((coupon) => coupon.couponCode),
      );
      let errorMsg = "";
      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        if (row.length === 0) continue;

        const [couponCode, password] = row;
        if (!couponCode || !password) {
          errorMsg = $t(
            "newPackage.validation.CSVFile.blankCouponError",
          ) as string;
          break;
        }

        if (row.length !== 2) {
          errorMsg = $t(
            "newPackage.validation.CSVFile.incompleteColumnError",
          ) as string;
          break;
        }

        if (codes.has(couponCode)) {
          errorMsg = $t(
            "newPackage.validation.CSVFile.duplicatedCouponsError",
          ) as string;
          break;
        }
        codes.add(couponCode);

        const validPattern = /^[A-Za-z0-9\-_]+$/;
        if (!validPattern.test(couponCode) || !validPattern.test(password)) {
          errorMsg = $t(
            "newPackage.validation.CSVFile.halfWithAlphanumbericError",
          ) as string;
          break;
        }
      }
      if (errorMsg) {
        setCSVInputErrorMessage(errorMsg);
        return;
      }
      setCSVInputErrorMessage("");
      setImportedCoupons(
        data.map((coupon: [string, string]) => ({
          couponCode: coupon[0],
          password: coupon[1],
        })),
      );
    },
  });
}
